import { VOICE_PARTS } from '@corocanto/scoring';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventDetail, MemberStatus } from '../api/events';
import type { VoicePart } from '../api/types';
import { Person } from '../components/Avatar';
import { FilterChips } from '../components/FilterChips';
import ui from '../styles/ui.module.css';
import styles from './EventPage.module.css';

/** A person with a minus sign: leave a member out of the event. */
const LeaveOutIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx={9} cy={8} r={3.5} />
    <path d="M2.5 19.5c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
    <path d="M16.5 11h5" />
  </svg>
);

/** Quick filters on the roster: everyone, or by answer. Left-out members show under "all" only. */
type RosterFilter = 'all' | 'ATTENDING' | 'none' | 'NOT_ATTENDING';

function matches(m: MemberStatus, filter: RosterFilter): boolean {
  if (filter === 'all') return true;
  if (m.excluded) return false;
  return filter === 'none' ? m.answer === null : m.answer === filter;
}

function answerClass(m: MemberStatus): string | undefined {
  switch (m.answer) {
    case 'ATTENDING':
      return ui.chipSoft;
    case 'NOT_ATTENDING':
      return ui.chipNeedsWork;
    default:
      return ui.chipNone;
  }
}

interface Editing {
  busy: boolean;
  onLeaveOut: (member: MemberStatus) => void;
  onTakeBack: (member: MemberStatus) => void;
}

/**
 * The event's line-up by section, with each singer's answer, note and readiness, and the two quick
 * filter rows. Read-only on the event page; on the "Edit line-up" page (`editing`) each row also
 * has "Leave out" or "Take back".
 */
export function EventRoster({ event, editing }: { event: EventDetail; editing?: Editing }) {
  const { t } = useTranslation();
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');
  const [section, setSection] = useState<VoicePart | null>(null);
  const lineup = event.members.filter((m) => m.inLineup);
  // The two filters combine; each row's counts follow the other row's choice.
  const inSection = (m: MemberStatus) => section === null || m.part === section;
  const shownMembers = lineup.filter((m) => matches(m, rosterFilter) && inSection(m));
  const outside = event.members.length - lineup.length;
  const songCount = event.program.filter((i) => i.kind === 'SONG').length;

  return (
    <section className={ui.card} data-testid="event-members">
      <h2 className={ui.cardTitle}>{t('eventPage.members')}</h2>
      <div className={styles.filterRows}>
        <FilterChips
          label={t('eventPage.filter.label')}
          value={rosterFilter}
          onChange={setRosterFilter}
          testId="roster-filters"
          options={(['all', 'ATTENDING', 'none', 'NOT_ATTENDING'] as const).map((key) => ({
            key,
            label: t(`eventPage.filter.${key}`),
            count: lineup.filter((m) => matches(m, key) && inSection(m)).length,
          }))}
        />
        <FilterChips
          label={t('members.filter')}
          value={section}
          onChange={setSection}
          testId="roster-sections"
          options={[
            { key: null, label: t('eventPage.filter.allSections'), count: lineup.filter((m) => matches(m, rosterFilter)).length },
            ...VOICE_PARTS.map((part) => ({
              key: part,
              label: t(`parts.${part}`),
              count: lineup.filter((m) => matches(m, rosterFilter) && m.part === part).length,
            })),
          ]}
        />
      </div>
      {shownMembers.length === 0 && <p className={ui.muted}>{t('eventPage.filter.empty')}</p>}
      {[...VOICE_PARTS, null].map((part) => {
        const group = shownMembers.filter((m) => m.part === part);
        if (group.length === 0) return null;
        return (
          <div key={part ?? 'none'} className={styles.section}>
            <div className={styles.sectionName}>{part ? t(`parts.${part}`) : '–'}</div>
            <table className={`${ui.table} ${styles.roster}`}>
              <colgroup>
                <col className={styles.colName} />
                <col className={styles.colAnswer} />
                <col />
                <col className={styles.colReady} />
                {editing && <col className={styles.colAction} />}
              </colgroup>
              <tbody>
                {group.map((m) => (
                  <tr key={m.userId} data-testid="member-row" className={m.excluded ? styles.excludedRow : undefined}>
                    <td>
                      <Person name={m.displayName} leadsSection={m.leadsSection} size="small">
                        {m.displayName}
                      </Person>
                    </td>
                    <td>
                      {m.excluded ? (
                        <span className={`${ui.chip} ${ui.chipNeutral}`}>{t('eventPage.excluded')}</span>
                      ) : (
                        <span className={`${ui.chip} ${answerClass(m)}`}>{t(`eventPage.answer.${m.answer ?? 'none'}`)}</span>
                      )}
                    </td>
                    <td className={styles.note}>{m.excluded ? m.exclusionReason : m.note}</td>
                    <td className={styles.ready}>{event.readinessTracking && songCount > 0 && t('eventPage.readySongs', { ready: m.readySongs, total: songCount })}</td>
                    {editing && (
                      <td className={styles.actionCell}>
                        {m.excluded ? (
                          <button type="button" className={ui.button} disabled={editing.busy} onClick={() => editing.onTakeBack(m)}>
                            {t('eventPage.include')}
                          </button>
                        ) : (
                          // Someone who is not coming has nothing to be left out of.
                          m.answer !== 'NOT_ATTENDING' && (
                            <button
                              type="button"
                              className={styles.iconButton}
                              aria-label={`${t('eventPage.exclude')}: ${m.displayName}`}
                              data-tip={t('eventPage.exclude')}
                              onClick={() => editing.onLeaveOut(m)}
                            >
                              <LeaveOutIcon />
                            </button>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      {event.limitedLineup && outside > 0 && <p className={ui.muted}>{t('eventPage.notInLineup', { count: outside })}</p>}
    </section>
  );
}
